import {array, string} from 'prop-types';
import {useState} from 'react';
import {useNavigate} from 'react-router-dom';

import {isNum} from '../../../../../lib/math.mjs';
import {TRAINING_EVENTS, UNIT} from '../../../../../lib/training.mjs';
import {useCreateMeso, useTrackEvent} from '../../api.js';
import {LS_KEY_EDIT_MESO_CREATION, WEEKDAYS} from '../../constants.js';
import {useMutationCallback} from '../../utils/hooks.js';
import {runAfterAnimations} from '../../utils/misc.js';
import storage from '../../utils/storage.js';
import useLocalStore from '../../utils/useLocalStore.js';
import Dialog from '../ui/Dialog.jsx';
import TextInput from '../ui/TextInput.jsx';

export default function CreateMesoModal({sex, weekDays}) {
  const navigate = useNavigate();
  const [weeks, setWeeks] = useState(4);
  const [unit, setUnit] = useState(UNIT.lb);

  const boardName = useLocalStore(st => st.boardName);
  const setBoardName = useLocalStore(st => st.setBoardName);
  const boardDays = useLocalStore(st => st.boardDays);
  const boardSourceTemplateId = useLocalStore(st => st.boardSourceTemplateId);
  const boardSourceMesoId = useLocalStore(st => st.boardSourceMesoId);
  const resetBoard = useLocalStore(st => st.resetBoard);

  const isOpen = useLocalStore(st => st.createMesoModalOpen);
  const setIsOpen = useLocalStore(st => st.setCreateMesoModalOpen);

  const progressions = useLocalStore(st => st.muscleGroupProgressions);
  const setProgressions = useLocalStore(st => st.setMuscleGroupProgressions);

  const createMeso = useCreateMeso();
  const labels = weekDays.map(wd => (isNum(wd) ? WEEKDAYS[wd] : null));
  const trackEvent = useTrackEvent();

  const handleClose = useMutationCallback(() => setIsOpen(false));
  const handleCreate = useMutationCallback(() => {
    const meso = {
      name: boardName,
      sex,
      weeks,
      days: boardDays.map(day => day.map(slot => slot.exerciseId)),
      weekDays: labels,
      unit,
      progressions,
      sourceTemplateId: boardSourceTemplateId,
      sourceMesoId: boardSourceMesoId,
    };

    createMeso.mutate(meso, {
      onSuccess: meso => {
        trackEvent.mutate({
          event: TRAINING_EVENTS.TRAINING_MESO_CREATED_AT,
          at: new Date().toISOString(),
          mesoKey: meso.key,
        });

        setIsOpen(false);

        runAfterAnimations(() => {
          storage.removeItem(LS_KEY_EDIT_MESO_CREATION);

          navigate(`/mesocycles/${meso.key}/weeks/1/days/1`, {replace: true});

          // TODO: This is hilarious. If we don't wait, we don't navigate
          runAfterAnimations(() => {
            resetBoard();
            setProgressions({}, {replace: true});
          });
        });
      },
    });
  }, [
    boardDays,
    boardName,
    boardSourceMesoId,
    boardSourceTemplateId,
    createMeso,
    labels,
    navigate,
    progressions,
    resetBoard,
    setIsOpen,
    setProgressions,
    sex,
    trackEvent,
    unit,
    weeks,
  ]);

  const isFormComplete = !!boardName;

  return (
    <Dialog isOpen={isOpen} onClose={handleClose} size="lg" title="Create your mesocycle">
      <div className="space-y-4 pt-6">
        <TextInput
          placeholder="Enter a name for this mesocycle"
          label="Mesocycle name"
          value={boardName ?? ''}
          onChange={setBoardName} // Note: Setting board name here is okay as long as we don't offer this from the template creation flow
          autoFocus
        />

        <div className="py-2">
          <p className="text-sm font-medium text-base-content">
            How many weeks will you train (including deload)?
          </p>

          <div className="btn-group mt-3 flex">
            <button
              onClick={() => setWeeks(4)}
              className={`btn btn-accent -ml-px grow items-center gap-1 ${
                weeks === 4 ? '' : 'btn-group-custom'
              }`}
            >
              4
            </button>
            <button
              onClick={() => setWeeks(5)}
              className={`btn btn-accent -ml-px grow items-center gap-1 ${
                weeks === 5 ? '' : 'btn-group-custom'
              }`}
            >
              5
            </button>
            <button
              onClick={() => setWeeks(6)}
              className={`btn btn-accent -ml-px grow items-center gap-1 ${
                weeks === 6 ? '' : 'btn-group-custom'
              }`}
            >
              6
            </button>
          </div>
        </div>
        <div className="py-2">
          <p className="text-sm font-medium text-base-content">
            What units do you use for measuring weight?
          </p>
          <div className="btn-group mt-3 flex">
            <button
              onClick={() => setUnit(UNIT.lb)}
              className={`btn btn-accent -ml-px grow items-center gap-1 ${
                unit === UNIT.lb ? '' : 'btn-group-custom'
              }`}
            >
              lb
            </button>
            <button
              onClick={() => setUnit(UNIT.kg)}
              className={`btn btn-accent -ml-px grow items-center gap-1 ${
                unit === UNIT.kg ? '' : 'btn-group-custom'
              }`}
            >
              kg
            </button>
          </div>
        </div>

        <div className="mt-8 flex items-center justify-end space-x-3 pt-2">
          <button className="btn btn-ghost" onClick={handleClose}>
            Cancel
          </button>

          <button
            className="btn btn-accent"
            disabled={!isFormComplete || createMeso.isWorking}
            onClick={handleCreate}
          >
            {createMeso.isWorking && <span className="loading"></span>}
            Create
          </button>
        </div>
      </div>
    </Dialog>
  );
}

CreateMesoModal.propTypes = {
  sex: string,
  weekDays: array.isRequired,
};
